import React, { useState, useEffect, useRef } from 'react';
import img1 from "../Whatsapp Marketing/images/custemorImg1.png";
import img2 from "../Whatsapp Marketing/images/custemorImg2.png";
import img3 from "../Whatsapp Marketing/images/custemorImg3.png";

const slides = [
    {
        image: img1,
        title: "HealthPlus Transformation",
        desc:
            "HealthPlus revolutionized patient care using our integrated healthcare solutions, improving outcomes and operational efficiency.",
        stats: [
            { k: "90%", v: "Patient Satisfaction" },
            { k: "-30%", v: "Response Time" },
            { k: "$2M", v: "Cost Savings" },
        ],
        cta: "Read the story",
    },
    {
        image: img2,
        title: "RetailGo Omnichannel Lift",
        desc:
            "RetailGo unified chat, order tracking and support to boost conversions and reduce support load during peak seasons.",
        stats: [
            { k: "+22%", v: "Checkout Conversion" },
            { k: "-35%", v: "Support Tickets" },
            { k: "4.8★", v: "CSAT" },
        ],
        cta: "Read the story",
    },
    {
        image: img3,
        title: "FinServe CX Modernization",
        desc:
            "FinServe automated onboarding and servicing on WhatsApp, cutting turnaround time and elevating customer experience.",
        stats: [
            { k: "3×", v: "Faster Onboarding" },
            { k: "-40%", v: "Ops Cost" },
            { k: "NPS 68", v: "Customer Loyalty" },
        ],
        cta: "Read the story",
    },
];

const CustemorStory = () => {
    const [idx, setIdx] = useState(0);
    const wrapRef = useRef(null);
    const timerRef = useRef(null); // autoplay timer

    // 👇 new: for reveal-on-scroll
    const sectionRef = useRef(null);
    const [revealed, setRevealed] = useState(false);

    const next = () => setIdx((p) => (p + 1) % slides.length);
    const prev = () => setIdx((p) => (p - 1 + slides.length) % slides.length);

    // keep autoplay cadence (restarts itself)
    const schedule = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            next();
            schedule();
        }, 2000);
    };

    // start autoplay and wire hover pause/resume
    useEffect(() => {
        const el = wrapRef.current;
        schedule();

        const onEnter = () => clearTimeout(timerRef.current);
        const onLeave = () => schedule();

        el?.addEventListener('mouseenter', onEnter);
        el?.addEventListener('mouseleave', onLeave);

        return () => {
            clearTimeout(timerRef.current);
            el?.removeEventListener('mouseenter', onEnter);
            el?.removeEventListener('mouseleave', onLeave);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 👇 reveal only when the section enters viewport from below while scrolling down
    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                const comingFromBelow = entry.boundingClientRect.top > 0;
                if (entry.isIntersecting && comingFromBelow) {
                    setRevealed(true);
                    io.unobserve(node); // run once
                }
            },
            {
                threshold: 0.35,
                rootMargin: '0px 0px -10% 0px',
            }
        );

        io.observe(node);
        return () => io.disconnect();
    }, []);

    const s = slides[idx];

    return (
        <section
            ref={sectionRef}
            className={`py-5 position-relative cs-bg reveal ${revealed ? 'show' : ''}`}
            style={{ overflow: 'hidden', marginTop: 80 }}
        >
            <div className="px-5 x-xxl-5 px-6 mx-auto position-relative" style={{ maxWidth: '74%', zIndex: 1 }}>
                {/* Heading */}
                <div style={{ textAlign: "center" }}>
                    <h2
                        style={{
                            fontSize: "2.5rem",
                            fontFamily: "Inter",
                            fontWeight: 700,
                            color: "#242121ff",
                            lineHeight: 1.2,
                        }}
                    >
                        Customer stories
                    </h2>
                    <p
                        className="mt-2"
                        style={{
                            marginTop: -2,
                            color: "#413e3eff",
                            fontSize: "1.1rem",
                            fontFamily: "Poppins",
                            fontWeight: 300,
                            lineHeight: 1.2,
                        }}
                    >
                        Real stories from happy clients showcasing trust, success and satisfaction.
                    </p>
                </div>

                {/* Carousel */}
                <div ref={wrapRef} className="cs-wrap">
                    <button
                        className="nav-btn left"
                        onClick={() => { prev(); schedule(); }}
                        aria-label="Previous"
                    >‹</button>

                    <div className="slide">
                        <div className="is-mg">
                            <img src={s.image} alt={s.title} loading="lazy" decoding="async" />
                        </div>
                        <div className="s-content">
                            <h3 className="s-title">{s.title}</h3>
                            <p className="s-desc">{s.desc}</p>

                            <div className="s-stats">
                                {s.stats.map((it, i) => (
                                    <div className="stat" key={i}>
                                        <div className="stat-k">{it.k}</div>
                                        <div className="stat-v">{it.v}</div>
                                    </div>
                                ))}
                            </div>

                            <button className="s-cta">{s.cta.toUpperCase()}</button>
                        </div>
                    </div>

                    <button
                        className="nav-btn right"
                        onClick={() => { next(); schedule(); }}
                        aria-label="Next"
                    >›</button>
                </div>

                {/* Dots */}
                <div className="cs-dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => { setIdx(i); schedule(); }}
                            aria-label={`Go to slide ${i + 1}`}
                            className={`dot ${i === idx ? 'active' : ''}`}
                        />
                    ))}
                </div>
            </div>

            <style>{`
        .cs-bg {
          background: linear-gradient(180deg,#F7FBFF 0%, #EAF3FF 100%);
        }

        /* 👇 fade-up only when revealed */
        .reveal {
          opacity: 0;
          transform: translateY(48px);
          transition: opacity .6s ease, transform .6s ease;
          will-change: opacity, transform;
        }
        .reveal.show {
          opacity: 1;
          transform: translateY(0);
        }

        .cs-wrap {
          margin-top: 40px;
          position: relative;
          display: grid;
          grid-template-columns: 48px 1fr 48px;
          align-items: center;
          gap: 12px;
        }

        .slide {
          display: grid;
          grid-template-columns: 1.15fr 1fr;
          gap: 36px;
          align-items: center;
        }

        .s-img img, .is-mg img {
          width: 100%;
          height: 370px;
          object-fit: cover;
          border-radius: 12px;
          box-shadow: 0 18px 40px rgba(15,35,90,.12);
          background: #fff;
        }

        .s-title {
          font-family: Lato;
          font-size: 1.8rem;
          font-weight: 700;
          color: #332f2fff;
          margin: 0 0 10px 0;
        }
        .s-desc {
          font-size: 1.1rem;
          font-family: Inter;
          line-height: 1.2;
          font-weight: 300;
          margin: 0 0 18px 0;
          color: #534f4fff;
        }

        .s-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0,1fr));
          gap: 16px;
          margin: 12px 0 18px;
        }
        .stat {
          background:#eef3fb;
          border:1px solid #dae1edff;
          border-radius:12px;
          padding:16px 10px;
        }
        .stat-k {
          font-size:18px;
          font-family: Poppins;
          font-weight:700;
          color:#054E91;
          margin-bottom:4px;
        }
        .stat-v {
          color: #534f4fff;
          font-size:15px;
          font-weight: 400;
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis; 
        }

        .s-cta {
          border: none;
          background:#054E91; 
          color:#fff;
          font-weight:580;
          border-radius:8px;
          padding:12px 16px;
          cursor:pointer;
        }

        .nav-btn {
          width: 36px; height: 36px;
          border-radius: 8px;
          border: none; cursor: pointer;
          background:#054E91;  
          color:#fff;
          font-size:20px;
          display:flex; align-items:center; justify-content:center;
        }

        .cs-dots {
          display:flex;
          justify-content:center;
          gap:6px; 
          margin-top:28px;
        }
        .dot {
          width:8px; height:8px; border-radius:999px; border:none;
          background:#b6c8e3; cursor:pointer;
          margin: 0; 
        }
        .dot.active {
          background:#0b5fd7;
        }
      `}</style>
        </section>
    );
};

export default CustemorStory;
